import React, { useMemo } from 'react'
import { Route, RouteComponentProps, RouteProps } from 'react-router-dom'
import { usePrivateRouteContext } from './PrivateRouteProvider'
import { PermissionConfig } from './types'
import { usePermissionExpressionValidator } from './util'

export interface PrivateRouteProps extends RouteProps {
  permissions?: PermissionConfig
  /* Don't check if user is authenticated */
  noAuthentication?: boolean
}

export const PrivateRoute: React.FC<PrivateRouteProps> = props => {
  const {
    children,
    render,
    component: Component,
    permissions,
    noAuthentication = false,
    ...rest
  } = props

  const {
    isAuthenticated,
    getAllPermissions,
    overrideAuthorization,
    notAuthenticatedComponent,
    notAuthorizedComponent,
  } = usePrivateRouteContext()

  const allPermissions = useMemo(() => getAllPermissions(), [getAllPermissions])
  const isAuthorized = usePermissionExpressionValidator(
    allPermissions,
    permissions,
    overrideAuthorization
  )

  const finalRender = useMemo(() => {
    if (!noAuthentication && !isAuthenticated)
      return () => notAuthenticatedComponent(props)
    if (!isAuthorized) return () => notAuthorizedComponent(props)

    if (typeof children !== 'undefined') return () => <>{children}</>
    if (typeof render !== 'undefined') return render
    if (typeof Component !== 'undefined' && Component !== null)
      return (props: RouteComponentProps<any>) => {
        const Comp = Component as any
        return <Comp {...props} />
      }

    throw new Error('Either children, render or component must be defined')
  }, [
    Component,
    children,
    isAuthenticated,
    isAuthorized,
    noAuthentication,
    notAuthenticatedComponent,
    notAuthorizedComponent,
    props,
    render,
  ])

  const R = Route as any
  return <R {...rest} render={finalRender} />
}
